import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { CONSENT_UI_TEXT, CONSENT_ALT_TEXT, CONSENT_UPLOAD_ACCEPT, CONSENT_SIGNATURE_MODES } from 'utils/constants'
import { mergeAndPrint } from '../utils/print'
import uploadIcon from '../../../assets/upload-consent.svg'
import printIcon from '../../../assets/print.svg'
import linedDocIcon from '../../../assets/lined-doc.svg'

const SignatureUpload = props => {
    const {
        readOnly,
        onSignUpdate,
        openModal,
        uploadedFile,
        setUploadedFile,
        isUploading,
        setIsUploading,
        consentName,
        pdfUrl,
        printToSign,
        promptText,
        consentId,
        consentVersion,
        dispatch
    } = props

    const [isPrinting, setIsPrinting] = useState(false)
    const [isDragOver, setIsDragOver] = useState(false)

    const getSignatureTypeFromFile = (file) => {
        const extension = (file?.name || '').toLowerCase().split('.').pop()
        const extensionMap = {
            pdf: CONSENT_SIGNATURE_MODES.pdf,
            png: CONSENT_SIGNATURE_MODES.image,
            jpg: CONSENT_SIGNATURE_MODES.image,
            jpeg: CONSENT_SIGNATURE_MODES.image
        }
        return extensionMap[extension] || CONSENT_SIGNATURE_MODES.photoUpload
    }

    const onUpdateUpload = file => {
        if (!file || !file.name) return
        setUploadedFile(file)
        setIsUploading(true)
        const reader = new FileReader()
        reader.onload = e => {
            const dataUrl = e?.target?.result || ''
            const base64 = typeof dataUrl === 'string' ? dataUrl.split(',')[1] : ''
            const mimeMatch = typeof dataUrl === 'string' ? dataUrl.match(/^data:([^;]+);/) : null
            const mimeType = mimeMatch ? mimeMatch[1] : file.type || null
            setIsUploading(false)
            const signatureType = getSignatureTypeFromFile(file)
            onSignUpdate({ value: base64, signature_type: signatureType, file_name: file.name, mime_type: mimeType })
        }
        reader.readAsDataURL(file)
    }

    const onRemoveUploadedFile = () => {
        if (!openModal || typeof openModal !== 'function') {
            console.error('openModal is not a function or is undefined')
            return
        }
        openModal({
            className: 'remove-uploaded-file',
            heading: CONSENT_UI_TEXT.removeConfirmHeading,
            content: CONSENT_UI_TEXT.removeConfirmText,
            confirmButton: CONSENT_UI_TEXT.removeConfirmRemove,
            cancelButton: CONSENT_UI_TEXT.removeConfirmCancel,
            closeOnBackgroundClick: true,
            humi: false,
            onConfirm: () => {
                setUploadedFile({})
                setIsUploading(false)
                onSignUpdate('')
            }
        })
    }

    const handlePrintWithSignaturePage = async () => {
        try {
            setIsPrinting(true)
            await mergeAndPrint({
                pdfUrl,
                consentName: promptText || consentName,
                consentId,
                consentVersion,
                dispatch
            })
        } finally {
            setIsPrinting(false)
        }
    }

    const handleUploadButtonClick = () => {
        if (uploadedFile?.name) return
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = CONSENT_UPLOAD_ACCEPT
        input.onchange = (e) => {
            const file = e.target.files[0]
            if (file) {
                onUpdateUpload(file)
            }
        }
        input.click()
    }

    const handleDragOver = (e) => {
        if (uploadedFile?.name) return
        e.preventDefault()
        setIsDragOver(true)
    }

    const handleDragLeave = () => {
        if (uploadedFile?.name) return
        setIsDragOver(false)
    }

    const handleDrop = (e) => {
        if (uploadedFile?.name) return
        e.preventDefault()
        setIsDragOver(false)
        const files = e.dataTransfer.files
        if (files.length > 0) {
            onUpdateUpload(files[0])
        }
    }

    return (
        <>
            {!readOnly && printToSign && (
                <div className='print-button'>
                    <button
                        onClick={handlePrintWithSignaturePage}
                        disabled={isPrinting}
                        aria-busy={isPrinting}
                    >
                        {isPrinting ? (
                            <>
                                <span className='spinner' aria-hidden='true' />
                                <span>{CONSENT_UI_TEXT.printToSign}</span>
                            </>
                        ) : (
                            <>
                                <span className='mode-icon'>
                                    <img src={printIcon} alt={CONSENT_ALT_TEXT.print} width={20} height={20} />
                                </span>
                                {CONSENT_UI_TEXT.printToSign}
                            </>
                        )}
                    </button>
                </div>
            )}
            <div className='signing-area custom-upload-area'>
                {uploadedFile?.name ? (
                    <div className='uploaded-file-display'>
                        <div className='uploaded-preview-zone'>
                            <button
                                className='remove-file-btn'
                                onClick={onRemoveUploadedFile}
                                aria-label='Remove uploaded file'
                                title='Remove uploaded file'
                            >
                                ✕
                            </button>
                            <div className='file-name'>{uploadedFile.name}</div>
                            <img src={linedDocIcon} alt={CONSENT_ALT_TEXT.fileIcon} className='file-icon' aria-hidden='true' />
                        </div>
                    </div>
                ) : (
                    <div
                        className={`upload-drop-zone ${isDragOver ? 'drag-over' : ''} ${uploadedFile?.name ? 'disabled' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <div className='upload-content'>
                            <p className='upload-text'>{CONSENT_UI_TEXT.uploadDropText}</p>
                            <p className='or-text'>{CONSENT_UI_TEXT.orText}</p>
                            <button
                                className={`upload-file-btn ${uploadedFile?.name ? 'disabled' : ''}`}
                                disabled={!!uploadedFile?.name}
                                onClick={handleUploadButtonClick}
                            >
                                <span className='upload-icon'>
                                    <img src={uploadIcon} alt={CONSENT_ALT_TEXT.upload} className='w-5 h-5' />
                                </span>
                                {CONSENT_UI_TEXT.uploadFile}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}

SignatureUpload.propTypes = {
    readOnly: PropTypes.bool,
    onSignUpdate: PropTypes.func,
    openModal: PropTypes.func,
    uploadedFile: PropTypes.object,
    setUploadedFile: PropTypes.func,
    isUploading: PropTypes.bool,
    setIsUploading: PropTypes.func,
    consentName: PropTypes.string,
    pdfUrl: PropTypes.string,
    printToSign: PropTypes.bool,
    promptText: PropTypes.string,
    consentId: PropTypes.string,
    consentVersion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dispatch: PropTypes.func
}

export default SignatureUpload
